import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LoginIcon from '@mui/icons-material/Login';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LockIcon from '@mui/icons-material/Lock';
import Brightness4Icon from '@mui/icons-material/Brightness4';

interface HelpSection {
  title: string;
  icon: React.ReactElement;
  steps: string[];
  tip?: string;
}

const sections: HelpSection[] = [
  {
    title: '1. Inicio de Sesion',
    icon: <LoginIcon color="primary" />,
    steps: [
      'Ingrese a la aplicacion desde su navegador.',
      'Escriba su usuario y contrasena de Odoo.',
      'Presione el boton "Iniciar Sesion".',
      'Si las credenciales son correctas, sera redirigido al Dashboard.',
    ],
    tip: 'Si olvido su contrasena, contacte al administrador del sistema para restablecerla desde Odoo.',
  },
  {
    title: '2. Dashboard',
    icon: <DashboardIcon color="primary" />,
    steps: [
      'Al ingresar vera el panel principal con estadisticas de ventas.',
      'Total Facturado: suma de todos los pedidos confirmados en el periodo.',
      'IGV: impuesto general a las ventas calculado automaticamente.',
      'Ticket Promedio: valor promedio por pedido.',
      'Use los filtros de fecha (mes/ano) para ver estadisticas de periodos especificos.',
      'Los graficos se actualizan automaticamente al cambiar los filtros.',
    ],
    tip: 'El Dashboard muestra solo los pedidos confirmados. Los pedidos en borrador no se incluyen en las estadisticas.',
  },
  {
    title: '3. Crear Pedido de Venta',
    icon: <AddShoppingCartIcon color="primary" />,
    steps: [
      'Vaya a "Crear Pedido" desde el menu.',
      'Seleccione un cliente existente usando el buscador.',
      'Agregue productos al pedido buscandolos por nombre o codigo.',
      'Ajuste la cantidad y la unidad de medida (UoM) de cada producto.',
      'Si aplica, ingrese un porcentaje de descuento por linea.',
      'Verifique el subtotal, IGV y total del pedido.',
      'Presione "Guardar como Borrador" para guardar sin confirmar.',
      'Presione "Confirmar Pedido" para confirmar directamente.',
    ],
    tip: 'Puede guardar el pedido como borrador y confirmarlo mas tarde desde la lista de pedidos.',
  },
  {
    title: '4. Ver Pedidos',
    icon: <ListAltIcon color="primary" />,
    steps: [
      'Vaya a "Ver Pedidos" desde el menu.',
      'Vera la lista de todos los pedidos creados.',
      'Use el filtro de mes para ver pedidos de un periodo especifico.',
      'Los estados posibles son: Borrador (editable), Confirmado (procesado), Cancelado.',
      'Haga clic en un pedido para ver su detalle completo.',
      'Use el boton de descarga (PDF) para obtener el comprobante del pedido.',
    ],
    tip: 'Los pedidos confirmados no pueden editarse. Solo los pedidos en estado Borrador permiten modificaciones.',
  },
  {
    title: '5. Editar / Eliminar Pedido',
    icon: <EditIcon color="primary" />,
    steps: [
      'Solo puede editar pedidos en estado "Borrador".',
      'Desde la lista de pedidos, haga clic en el icono de editar.',
      'Modifique los productos, cantidades, descuentos o cliente segun necesite.',
      'Guarde los cambios o confirme el pedido.',
      'Para eliminar, use el boton de eliminar en pedidos en borrador.',
    ],
    tip: 'Una vez confirmado, el pedido no puede editarse ni eliminarse. Asegurese de revisar todos los datos antes de confirmar.',
  },
  {
    title: '6. Cancelar Pedido',
    icon: <CancelIcon color="primary" />,
    steps: [
      'Solo puede cancelar pedidos que han sido confirmados.',
      'Desde el detalle del pedido, presione "Cancelar Pedido".',
      'Se generara automaticamente una nota de credito en Odoo.',
      'El stock de los productos sera devuelto al inventario.',
      'El pedido cambiara a estado "Cancelado".',
    ],
    tip: 'La cancelacion es irreversible. Se creara una nota de credito automatica para revertir la facturacion.',
  },
  {
    title: '7. Crear Cliente',
    icon: <PersonAddIcon color="primary" />,
    steps: [
      'Vaya a "Crear Cliente" desde el menu.',
      'Seleccione el tipo de documento: RUC (empresa) o DNI (persona).',
      'Ingrese el numero de documento y presione "Buscar" para autocompletar datos.',
      'Complete los campos obligatorios: nombre, direccion, tipo de comprobante.',
      'El tipo de comprobante se asigna automaticamente (Factura para RUC, Boleta para DNI).',
      'Presione "Guardar" para crear el cliente en Odoo.',
    ],
    tip: 'Al buscar por RUC o DNI, los datos se completan automaticamente desde SUNAT. Verifique que la informacion sea correcta antes de guardar.',
  },
  {
    title: '8. Cambiar Contrasena',
    icon: <LockIcon color="primary" />,
    steps: [
      'En escritorio: haga clic en "Mi Perfil" en la barra superior.',
      'En movil: abra el menu lateral y seleccione "Cambiar Contrasena".',
      'Ingrese su contrasena actual.',
      'Ingrese la nueva contrasena (minimo 6 caracteres).',
      'Confirme la nueva contrasena.',
      'Presione "Cambiar". Se cerrara la sesion automaticamente.',
    ],
    tip: 'Despues de cambiar la contrasena, debera iniciar sesion nuevamente con la nueva contrasena.',
  },
  {
    title: '9. Modo Oscuro / Claro',
    icon: <Brightness4Icon color="primary" />,
    steps: [
      'En escritorio: haga clic en "Mi Perfil" y seleccione "Modo Oscuro" o "Modo Claro".',
      'En movil: abra el menu lateral y seleccione la opcion de tema.',
      'El cambio se aplica inmediatamente en toda la aplicacion.',
      'Su preferencia se mantiene guardada para futuras sesiones.',
    ],
  },
];

const HelpPage: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
      <Paper sx={{ p: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <HelpOutlineIcon color="primary" sx={{ fontSize: 36 }} />
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Manual de Uso
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Bienvenido al manual de uso del Sistema de Ventas. Seleccione una seccion para ver las instrucciones detalladas.
        </Typography>

        {sections.map((section, index) => (
          <Accordion key={index} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {section.icon}
                <Typography variant="subtitle1" fontWeight="bold">
                  {section.title}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box component="ol" sx={{ pl: 2, m: 0 }}>
                {section.steps.map((step, stepIndex) => (
                  <Typography component="li" key={stepIndex} sx={{ mb: 1 }}>
                    {step}
                  </Typography>
                ))}
              </Box>
              {section.tip && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Nota:</strong> {section.tip}
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </Container>
  );
};

export default HelpPage;
